import { Link } from "@inertiajs/react";
import {
	ArrowLeft,
	ExternalLink,
	Music,
	TrendingUp,
	Users,
} from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { search } from "@/routes.js";
import type { Artist, Song } from "@/types";

interface ArtistShowProps {
	artist: Artist | null;
	songs: Song[];
	total_songs: number;
	error?: string;
}

export default function Show({
	artist,
	songs,
	total_songs,
	error,
}: ArtistShowProps) {
	if (error) {
		return (
			<div className="container mx-auto py-8 px-4 max-w-4xl">
				<Button variant="ghost" className="mb-4" asChild>
					<Link href={search()}>
						<ArrowLeft className="mr-2 h-4 w-4" />
						Back to Search
					</Link>
				</Button>

				<Alert variant="destructive">
					<AlertTitle>Error</AlertTitle>
					<AlertDescription>{error}</AlertDescription>
				</Alert>
			</div>
		);
	}

	if (!artist) {
		return (
			<div className="container mx-auto py-8 px-4 max-w-4xl">
				<p>Artist not found</p>
			</div>
		);
	}

	return (
		<div className="container mx-auto py-8 px-4 max-w-6xl">
			<Button variant="ghost" className="mb-4" asChild>
				<Link href={search()}>
					<ArrowLeft className="mr-2 h-4 w-4" />
					Back to Search
				</Link>
			</Button>

			{/* Artist Header */}
			<Card className="mb-6">
				<CardHeader>
					<div className="flex flex-col md:flex-row gap-6">
						<Avatar className="w-24 h-24">
							<AvatarImage
								src={artist.thumbnail || undefined}
								alt={artist.name}
							/>
							<AvatarFallback>
								{artist.name.substring(0, 2).toUpperCase()}
							</AvatarFallback>
						</Avatar>
						<div className="flex-1">
							<CardTitle className="text-3xl mb-2">{artist.name}</CardTitle>
							{artist.description && (
								<CardDescription className="text-base line-clamp-3">
									{artist.description}
								</CardDescription>
							)}
							<div className="flex gap-4 mt-4">
								{artist.follower_count && (
									<div className="flex items-center gap-1 text-sm text-muted-foreground">
										<Users className="h-4 w-4" />
										<span>
											{artist.follower_count.toLocaleString()} followers
										</span>
									</div>
								)}
								{artist.iq_points && (
									<div className="flex items-center gap-1 text-sm text-muted-foreground">
										<TrendingUp className="h-4 w-4" />
										<span>{artist.iq_points.toLocaleString()} IQ</span>
									</div>
								)}
							</div>
						</div>
						<div>
							<Button variant="outline" asChild>
								<a href={artist.url} target="_blank" rel="noopener noreferrer">
									View on Genius
									<ExternalLink className="ml-2 h-4 w-4" />
								</a>
							</Button>
						</div>
					</div>
				</CardHeader>
			</Card>

			{/* Songs Section */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<Music className="h-5 w-5" />
						All Songs
						<Badge variant="secondary" className="ml-2">
							{total_songs}
						</Badge>
					</CardTitle>
					<CardDescription>
						Complete discography sorted by popularity
					</CardDescription>
				</CardHeader>
				<CardContent>
					{songs.length === 0 ? (
						<p className="text-center text-muted-foreground py-8">
							No songs found for this artist.
						</p>
					) : (
						<div className="space-y-4">
							{songs.map((song, index) => (
								<div key={song.id}>
									{index > 0 && <Separator />}
									<div className="flex items-start gap-4 py-2">
										{song.thumbnail && (
											<img
												src={song.thumbnail}
												alt={song.title}
												className="w-12 h-12 rounded object-cover flex-shrink-0"
											/>
										)}
										<div className="flex-1 min-w-0">
											<h3 className="font-semibold text-base truncate">
												{song.title}
											</h3>
											{song.artist_names && (
												<p className="text-sm text-muted-foreground">
													{song.artist_names}
												</p>
											)}
											{song.release_date && (
												<p className="text-xs text-muted-foreground mt-1">
													Released: {song.release_date}
												</p>
											)}
										</div>
										<div className="flex items-center gap-2">
											{song.page_views && (
												<Badge variant="outline" className="text-xs">
													{song.page_views.toLocaleString()} views
												</Badge>
											)}
											<Button variant="ghost" size="sm" asChild>
												<a
													href={song.url}
													target="_blank"
													rel="noopener noreferrer"
												>
													<ExternalLink className="h-4 w-4" />
												</a>
											</Button>
										</div>
									</div>
								</div>
							))}
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
